import { ArticlePreviewResponse, ContentResponse, ContentType, SourceType, UrlParser } from "@authory-packages/core";
import { detectFaviconImageError } from "../../hooks/detectFaviconImageError";
import { theme } from "../../theme/theme";
import { V3IconRedirect } from "../Icons";
import { getContentData } from "../../utils/article-text-utils";
import { V3Tooltip } from "../Tooltip";
import { OptionalLink } from "../ReusableStyledComponents/OptionalLink";
import {
    PLIMetadata,
    PLIFavicon,
    PLISourceMetaDateWrapper,
    PLIMetadataText,
    PLISourceMetaWrapper,
    PLISourceMetaIconWrapper,
    PLISmallAvatar,
    PLISourceOriginalWrapper,
    PLIMetadataOriginalLink,
    RedirectIconLink,
    PLIDateCompact,
    PLIDateFull
} from "../V3ProfileListItem/V3ProfileListItem.styles";
import { renderArticleDate } from "../../utils/articleDateParser";
import { EmbedTheme } from "../../utils/embeds";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";

interface ItemMetadata {
    item: ArticlePreviewResponse | ContentResponse | { originalUrl: string | null; sourceName?: string; date: string | null };
    showRedirect?: boolean;
    isB2B?: boolean;
    hideFavicon?: boolean;
    hideContentType?: boolean;
    customDateFormat?: string;
    compactDateFormat?: string;
    isPrivateView?: boolean;
    hideUserAvatar?: boolean;
    viewOriginal?: boolean;
    embedTheme?: EmbedTheme;
    isEmbed?: boolean;
    allowUserThemable?: boolean;
    colorVariant?: AuthoryColorSchemes;
    customBackgroundColor?: string;
    byline?: { name: string; href: string };
}

export const ItemMetadata = ({
    item,
    showRedirect = true,
    isB2B,
    hideFavicon = false,
    customDateFormat = "MMMM dd, yyyy",
    compactDateFormat,
    isPrivateView = false,
    hideUserAvatar = false,
    hideContentType = false,
    viewOriginal = false,
    isEmbed = false,
    embedTheme = EmbedTheme.light,
    colorVariant = AuthoryColorSchemes.userTheme,
    allowUserThemable = false,
    customBackgroundColor,
    byline
}: ItemMetadata) => {
    let isOffline = false;
    let domain = "";

    try {
        try {
            domain = UrlParser.extractDomain(item.originalUrl) || "";
        } catch {
            domain = "";
        }
    } catch {}

    if (
        (!!domain.length && domain.includes("authory.com")) ||
        ("sourceType" in item && item.sourceType === SourceType.Offline) ||
        ("sourceName" in item && typeof item.sourceName !== "string" && "sourceType" in item && item.sourceType === SourceType.Offline)
    ) {
        isOffline = true;
    }

    const imgSrc = detectFaviconImageError(item.originalUrl || "", isOffline);

    const RedirectTooltip = () => (
        <V3Tooltip tooltipMaxWidth={320} referenceContent={<V3IconRedirect />} tooltipContent={"This preview links out to the original content."} />
    );

    const sourceLabel = isB2B && "owner" in item && item.owner ? `${item.owner.firstName} ${item.owner.lastName}` : item.sourceName;
    const contentDate = renderArticleDate(item.date, customDateFormat);
    const compactDate = compactDateFormat ? renderArticleDate(item.date, compactDateFormat) : "";

    return (
        <PLIMetadata $isEmbed={isEmbed} $embedTheme={embedTheme}>
            {byline && (
                <PLISourceMetaDateWrapper>
                    <PLIMetadataText
                        $allowUserThemable={allowUserThemable}
                        $colorVariant={colorVariant}
                        $customBackgroundColor={customBackgroundColor}
                        $customDarkShade={CustomDarkShade.Grey48}
                    >
                        By{" "}
                        <OptionalLink href={byline.href}>
                            <strong>{byline.name}</strong>
                        </OptionalLink>
                    </PLIMetadataText>
                </PLISourceMetaDateWrapper>
            )}
            {!!item.originalUrl && !isB2B && !hideFavicon && <PLIFavicon $faviconUrl={imgSrc || ""} suppressHydrationWarning />}
            {isB2B && !hideUserAvatar && "owner" in item && item.owner && !!item.owner.avatarUrl.length && (
                <PLISmallAvatar profileSrc={item.owner.avatarUrl[0].url} />
            )}
            {!!contentDate && (
                <PLISourceMetaDateWrapper>
                    <PLIMetadataText
                        suppressHydrationWarning
                        $allowUserThemable={allowUserThemable}
                        $colorVariant={colorVariant}
                        $customBackgroundColor={customBackgroundColor}
                        $customDarkShade={CustomDarkShade.Grey48}
                    >
                        <time dateTime={item.date?.substring(0, 10)}>
                            {compactDate ? (
                                <>
                                    <PLIDateCompact suppressHydrationWarning>{compactDate}</PLIDateCompact>
                                    <PLIDateFull suppressHydrationWarning>{contentDate}</PLIDateFull>
                                </>
                            ) : (
                                contentDate
                            )}
                        </time>
                    </PLIMetadataText>
                </PLISourceMetaDateWrapper>
            )}
            <PLISourceMetaWrapper>
                <PLIMetadataText
                    $allowUserThemable={allowUserThemable}
                    $colorVariant={colorVariant}
                    $customBackgroundColor={customBackgroundColor}
                    $customDarkShade={CustomDarkShade.Grey48}
                >
                    {!hideContentType && (
                        <>
                            {getContentData("type" in item ? item.type : ContentType.Article).label} {isB2B ? "by " : "at "}
                        </>
                    )}
                    {!!sourceLabel ? (
                        <>
                            <strong>{sourceLabel}</strong>
                        </>
                    ) : (
                        <></>
                    )}
                </PLIMetadataText>
            </PLISourceMetaWrapper>
            {viewOriginal && item.originalUrl && (
                <PLIMetadataOriginalLink
                    as="a"
                    target="_blank"
                    href={item.originalUrl}
                    rel="nofollow"
                    $allowUserThemable={allowUserThemable}
                    $colorVariant={colorVariant}
                    $customBackgroundColor={customBackgroundColor}
                    $customDarkShade={CustomDarkShade.Grey48}
                >
                    <PLISourceOriginalWrapper>
                        <div>
                            <V3IconRedirect stroke={theme.colors.blackPearl3} />
                        </div>
                        <div>View original</div>
                    </PLISourceOriginalWrapper>
                </PLIMetadataOriginalLink>
            )}
            {showRedirect && !isPrivateView && (
                <PLISourceMetaIconWrapper>
                    <RedirectIconLink href={item.originalUrl || "#"} target="_blank" rel="nofollow">
                        <RedirectTooltip />
                    </RedirectIconLink>
                </PLISourceMetaIconWrapper>
            )}
        </PLIMetadata>
    );
};
